import { create } from "zustand";
import type { TeleopGroup, TeleopStatusBody } from "@tcr/contracts";
import type { LinkState } from "./manager";

/** Robot-side teleop watchdog state of one motion group (realtime `teleop.status`, body = TeleopStatusBody). */
export interface TeleopStatus {
  leaseId: string;
  /** Absent on robots before arm jog: means `base`. */
  group?: TeleopGroup;
  active: boolean;
  lastSeq: number;
  stoppedReason?: TeleopStatusBody["stoppedReason"];
  at: string;
}

/** Arm jog halts reported by MoveIt Servo. The jog session continues; `active: true` follows when it clears. */
export const ARM_HALT_REASONS: ReadonlySet<string> = new Set(["singularity", "joint_limit", "collision"]);

/** Teleop status of one robot, per motion group: base and arm reports no longer overwrite each other. */
export interface RobotTeleopStatus {
  base?: TeleopStatus;
  arm?: TeleopStatus;
  /**
   * Last Servo halt of the arm jog (singularity / joint_limit / collision). Kept across the
   * operator's own stop (which reports platform_stop) so the hint stays visible; cleared when the
   * robot reports the arm active again or a status arrives for another lease.
   */
  armHalt?: TeleopStatus;
}

interface RealtimeState {
  link: LinkState;
  rttMs: number | null;
  lastEventAt: number | null;
  deniedTopics: string[];
  lastError: { errorCode: string; errorMessage: string; at: number } | null;
  teleop: Record<string, RobotTeleopStatus>;
  setLink: (s: LinkState) => void;
  setRtt: (ms: number) => void;
  markEvent: () => void;
  setDenied: (topics: string[]) => void;
  setError: (e: { errorCode: string; errorMessage: string }) => void;
  setTeleop: (robotId: string, s: TeleopStatus) => void;
}

/** Merge one teleop.status into a robot's per-group status (absent group = base). */
export function mergeTeleopStatus(prev: RobotTeleopStatus | undefined, s: TeleopStatus): RobotTeleopStatus {
  const group: TeleopGroup = s.group ?? "base";
  const next: RobotTeleopStatus = { ...prev, [group]: s };
  if (group === "arm") {
    if (!s.active && s.stoppedReason && ARM_HALT_REASONS.has(s.stoppedReason)) next.armHalt = s;
    else if (s.active || (prev?.armHalt && prev.armHalt.leaseId !== s.leaseId)) delete next.armHalt;
  }
  return next;
}

export const useRealtimeStore = create<RealtimeState>((set, get) => ({
  link: "idle",
  rttMs: null,
  lastEventAt: null,
  deniedTopics: [],
  lastError: null,
  teleop: {},
  setLink: (link) => set({ link }),
  setRtt: (rttMs) => set({ rttMs }),
  markEvent: () => set({ lastEventAt: Date.now() }),
  setDenied: (topics) => set({ deniedTopics: [...new Set([...get().deniedTopics, ...topics])] }),
  setError: (e) => set({ lastError: { ...e, at: Date.now() } }),
  setTeleop: (robotId, s) => set({ teleop: { ...get().teleop, [robotId]: mergeTeleopStatus(get().teleop[robotId], s) } }),
}));
